import React from "react";
import { Shield, FileText, Scale, CircleAlert as AlertCircle, Mail, ChevronRight, Download } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const guidelinesData = [{
  id: "ruang-lingkup",
  title: "1. Ruang Lingkup",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>
          a. Media Siber adalah segala bentuk media yang menggunakan wahana internet dan melaksanakan kegiatan jurnalistik, serta memenuhi persyaratan Undang-Undang Pers dan Standar Perusahaan Pers yang ditetapkan Dewan Pers.
        </p>
        <p>
          b. Isi Buatan Pengguna (User Generated Content) adalah segala isi yang dibuat dan atau dipublikasikan oleh pengguna media siber, antara lain, artikel, gambar, komentar, suara, video dan berbagai bentuk unggahan yang melekat pada media siber, seperti blog, forum, komentar pembaca atau pemirsa, dan bentuk lain.
        </p>
      </div>
}, {
  id: "verifikasi",
  title: "2. Verifikasi dan Keberimbangan Berita",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>a. Pada prinsipnya setiap berita harus melalui verifikasi.</p>
        <p>b. Berita yang dapat merugikan pihak lain memerlukan verifikasi pada berita yang sama untuk memenuhi prinsip akurasi dan keberimbangan.</p>
        <p>c. Ketentuan dalam butir (a) di atas dikecualikan, dengan syarat:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Berita benar-benar mengandung kepentingan publik yang bersifat mendesak;</li>
          <li>Sumber berita yang pertama adalah sumber yang jelas disebutkan identitasnya, kredibel dan kompeten;</li>
          <li>Subyek berita yang harus dikonfirmasi tidak diketahui keberadaannya dan atau tidak dapat diwawancarai;</li>
          <li>Media memberikan penjelasan kepada pembaca bahwa berita tersebut masih memerlukan verifikasi lebih lanjut yang diupayakan dalam waktu secepatnya. Penjelasan dimuat pada bagian akhir dari berita yang sama, di dalam kurung dan menggunakan huruf miring.</li>
        </ul>
        <p>d. Setelah memuat berita sesuai dengan butir (c), media wajib meneruskan upaya verifikasi, dan setelah verifikasi didapatkan, hasil verifikasi dicantumkan pada berita pemutakhiran (update) dengan tautan pada berita yang belum terverifikasi.</p>
      </div>
}, {
  id: "ugc",
  title: "3. Isi Buatan Pengguna (User Generated Content)",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>a. Media siber wajib mencantumkan syarat dan ketentuan mengenai Isi Buatan Pengguna yang tidak bertentangan dengan Undang-Undang No. 40 tahun 1999 tentang Pers dan Kode Etik Jurnalistik, yang ditempatkan secara terang dan jelas.</p>
        <p>b. Media siber mewajibkan setiap pengguna untuk melakukan registrasi keanggotaan dan melakukan proses log-in terlebih dahulu untuk dapat mempublikasikan semua bentuk Isi Buatan Pengguna. Ketentuan mengenai log-in akan diatur lebih lanjut.</p>
        <p>c. Dalam registrasi tersebut, media siber mewajibkan pengguna memberi persetujuan tertulis bahwa Isi Buatan Pengguna yang dipublikasikan:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Tidak memuat isi bohong, fitnah, sadis dan cabul;</li>
          <li>Tidak memuat isi yang mengandung prasangka dan kebencian terkait dengan suku, agama, ras, dan antargolongan (SARA), serta menganjurkan tindakan kekerasan;</li>
          <li>Tidak memuat isi diskriminatif atas dasar perbedaan jenis kelamin dan bahasa, serta tidak merendahkan martabat orang lemah, miskin, sakit, cacat jiwa, atau cacat jasmani.</li>
        </ul>
      </div>
}, {
  id: "ralat",
  title: "4. Ralat, Koreksi, dan Hak Jawab",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>a. Ralat, koreksi, dan hak jawab mengacu pada Undang-Undang Pers, Kode Etik Jurnalistik, dan Pedoman Hak Jawab yang ditetapkan Dewan Pers.</p>
        <p>b. Ralat, koreksi dan atau hak jawab wajib ditautkan pada berita yang diralat, dikoreksi atau yang diberi hak jawab.</p>
        <p>c. Di setiap berita yang diralat, dikoreksi, dan atau diberi hak jawab wajib dicantumkan waktu pemuatan ralat, koreksi, dan atau hak jawab tersebut.</p>
        <p>d. Bila suatu berita media siber tertentu disebarluaskan media siber lain, maka:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Tanggung jawab media siber pembuat berita terbatas pada berita yang dipublikasikan di media siber tersebut atau media siber yang berada di bawah otoritas teknisnya;</li>
          <li>Koreksi berita yang dilakukan oleh sebuah media siber, juga harus dilakukan oleh media siber lain yang mengutip berita dari media siber yang dikoreksi itu;</li>
          <li>Media yang menyebarluaskan berita dari sebuah media siber dan tidak melakukan koreksi atas berita sesuai yang dilakukan oleh media siber pemilik dan atau pembuat berita tersebut, bertanggung jawab penuh atas semua akibat hukum dari berita yang tidak dikoreksinya itu.</li>
        </ul>
      </div>
}, {
  id: "pencabutan",
  title: "5. Pencabutan Berita",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>a. Berita yang sudah dipublikasikan tidak dapat dicabut karena alasan penyensoran dari pihak luar redaksi, kecuali terkait masalah SARA, kesusilaan, masa depan anak, pengalaman traumatik korban atau berdasarkan pertimbangan khusus lain yang ditetapkan Dewan Pers.</p>
        <p>b. Media siber lain yang terlanjur menyebarluaskan berita yang dicabut tersebut wajib mencabut berita yang sama.</p>
        <p>c. Pencabutan berita wajib disertai dengan alasan pencabutan dan diumumkan kepada publik.</p>
      </div>
}, {
  id: "iklan",
  title: "6. Iklan",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>a. Media siber wajib membedakan dengan tegas antara produk berita dan iklan.</p>
        <p>b. Setiap berita/artikel/isi yang merupakan iklan dan atau isi berbayar wajib mencantumkan keterangan "advertorial", "iklan", "ads", "sponsored", atau kata lain yang menjelaskan bahwa berita/artikel/isi tersebut adalah iklan.</p>
      </div>
}, {
  id: "hak-cipta",
  title: "7. Hak Cipta",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>Media siber wajib menghormati hak cipta sebagaimana diatur dalam peraturan perundang-undangan yang berlaku.</p>
      </div>
}, {
  id: "pencantuman",
  title: "8. Pencantuman Pedoman",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>Media siber wajib mencantumkan Pedoman Pemberitaan Media Siber ini di medianya secara terang dan jelas.</p>
      </div>
}, {
  id: "sengketa",
  title: "9. Sengketa",
  content: <div className="space-y-4 text-muted-foreground leading-relaxed">
        <p>Penilaian akhir atas sengketa mengenai pelaksanaan Pedoman Pemberitaan Media Siber ini diselesaikan oleh Dewan Pers.</p>
      </div>
}];
export default function CyberMediaGuidelines() {
  return <section id="cyber-media-guidelines" className="bg-background py-12 md:py-20 lg:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        
        {}
        <div className="space-y-6 mb-12">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Beranda</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Pedoman Media Siber</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="rounded-md px-3 py-1">
                <Scale className="w-3.5 h-3.5 mr-1.5" />
                Regulasi & Etika
              </Badge>
              <span className="text-sm text-muted-foreground">Pembaruan Terakhir: 12 Mei 2024</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">
              Pedoman Pemberitaan Media Siber
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
              Kemerdekaan berpendapat, kemerdekaan berekspresi, dan kemerdekaan pers adalah hak asasi manusia yang dilindungi Pancasila, Undang-Undang Dasar 1945, dan Deklarasi Universal Hak Asasi Manusia PBB. ABADI.NEWS berkomitmen penuh mematuhi pedoman ini.
            </p>
          </div>
        </div>

        <Separator className="mb-12" />

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {}
          <div className="lg:col-span-8 space-y-8">
            <div className="prose prose-neutral dark:prose-invert max-w-none">
              <p className="text-foreground leading-relaxed mb-6">
                Keberadaan media siber di Indonesia juga merupakan bagian dari kemerdekaan berpendapat, kemerdekaan berekspresi, dan kemerdekaan pers. Media siber memiliki karakter khusus sehingga memerlukan pedoman agar pengelolaannya dapat dilaksanakan secara profesional, memenuhi fungsi, hak, dan kewajibannya sesuai Undang-Undang Nomor 40 Tahun 1999 tentang Pers dan Kode Etik Jurnalistik.
              </p>
              <p className="text-foreground leading-relaxed mb-8">
                Untuk itu Dewan Pers bersama organisasi pers, pengelola media siber, dan masyarakat menyusun Pedoman Pemberitaan Media Siber sebagai berikut:
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full" defaultValue="ruang-lingkup">
              {guidelinesData.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-border">
                  <AccordionTrigger className="text-left font-default text-lg font-semibold hover:text-primary transition-colors py-5">
                    {item.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-base pb-6">
                    {item.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div className="lg:col-span-4 space-y-6">
            <Card className="border-border shadow-sm bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="font-default text-xl flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Komitmen Etik
                </CardTitle>
                <CardDescription>
                  ABADI.NEWS tunduk pada hukum dan regulasi pers yang berlaku di Indonesia.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3 text-sm text-muted-foreground">
                  <FileText className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>Pedoman ini disusun dan ditetapkan oleh Dewan Pers Indonesia.</p>
                </div>
                <div className="flex items-start gap-3 text-sm text-muted-foreground">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>Setiap jurnalis dan editor kami wajib memahami dan menerapkan pedoman ini dalam setiap karya jurnalistik.</p>
                </div>
                <Separator className="my-4" />
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link to="#">
                    <Download className="w-4 h-4 mr-2" />
                    Unduh Dokumen PDF
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border shadow-sm bg-secondary/5 border-none">
              <CardHeader className="pb-4">
                <CardTitle className="font-default text-lg">Layanan Pengaduan</CardTitle>
                <CardDescription>
                  Terkait sengketa pemberitaan atau pelanggaran kode etik.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Jika Anda menemukan konten yang melanggar pedoman ini, silakan hubungi tim redaksi kami untuk proses ralat, koreksi, atau hak jawab.
                </p>
                <Button className="w-full" asChild>
                  <Link to="/ClarificationPage">
                    <Mail className="w-4 h-4 mr-2" />
                    Kirim Klarifikasi
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}